import './WordPartDeletion.scss'
import { COPY_STRINGS } from '../../../../../constants/translatedCopy'
import get from 'lodash.get'
import React, { useEffect, useMemo, useRef } from 'react'
import Spot, {useSpot} from '../../../../Spot'
import useDidAttempt from '../../hooks/useDidAttempt'
import usePauseControl from '../../hooks/usePauseControl'
import usePhraseTypeIndexes from '../../hooks/usePhraseTypeIndexes'
import { phraseTypes } from '../../../../../constants/constants'
import { useSelector } from 'react-redux'
import { selectReaderManager } from '../../../ReaderManagerEvents/ReaderManagerSlice'
import useEndingSpeech from '../../hooks/useEndingSpeech'
import useIsListening from '../useIsListening'
import { parseTextWithSkipTranslationAndPhonemes } from '../../../../../services/util'
import { getUserData } from '../../../../../services/API'
import { getCopyByGradeband } from '../../../../../services/ThemingService'

let startTime = null

export default function WordPartDeletion(props) {

  const {
    speech,
    currentPageIndex,
    targetPart,
    phraseMetadata,
    transcript,
    isPlayingVerbalInstructions,
    startPhrase,
    wordPartToDelete,
    speakWithPhoneme
} = props
const {
    paused,
    setWillHandlePause,
    pausePhrase,
    resumePhrase,
    doneHandlingPause
} = props;

    const readerManagerStates = useSelector(selectReaderManager)
    const { contextStates } = readerManagerStates;
    const { currentStoryLoaded } = contextStates;

    const isListening = useIsListening()
    const { addToEndingSequence, hasPendingEndingSequence } = useEndingSpeech()
    const { didAttempt } = useDidAttempt({transcript})
    const { shouldHandlePause, finishHandlePause, passPauseControlBackToReaderManager } = usePauseControl({
        paused,
        setWillHandlePause,
        pausePhrase,
        resumePhrase,
        doneHandlingPause
    })

    const {
        isExamplePhraseOfThisType,
        isLastPhraseOfThisType,
        isLastPageOfStory,
    } = usePhraseTypeIndexes(currentPageIndex, phraseTypes.WORD_PART_DELETION)

    const didPlayInstruction = useRef(false)
    const didHandlePause = useRef(false)

    const originalWord = (() => {
        let word = get(phraseMetadata, 'instructions.text', '');
        if (!word) {
            word = get(phraseMetadata, 'startingWord', '');
        }
        return word;
    })();
    const omitPart = useMemo(() => {
        if (!originalWord || !targetPart) return '';

        if (wordPartToDelete && wordPartToDelete !== targetPart) {
            return wordPartToDelete;
        }

        return originalWord.replace(targetPart, '')
    }, [originalWord, targetPart])

    /**
     * initialize Spot, start with awake state
     */
    const {spotState, assets: { bark }} = useSpot(speech, "AWAKE")

    /**
     * handle when the phrase is started, setup the mid phrase timeout
     */
    useEffect(() => {
        const word_part_deletion_copy = getCopyByGradeband(COPY_STRINGS, 'word_part_deletion');
        if(didPlayInstruction.current) return; // do not play again if we already played the instruction
        if(hasPendingEndingSequence) return; // hold off from start if the previous ending speech is still playing
        if(!currentStoryLoaded) return; // Don't start until we have the latest story loaded (handles case where AOS is loading a new story)
        if(!isPlayingVerbalInstructions){
            didPlayInstruction.current = true
            speech.stopListening()

            const playInstruction = async () => {
                if (isExamplePhraseOfThisType) {
                    await speech.speakWithPromise({
                        textToSpeak: word_part_deletion_copy.intro_instruction_1,
                    });
                }

                const skipTranslation = getUserData().bilingualMode;

                // If omitPart does not contain a phoneme tag, we need to wrap it in quotes for GenAI TTS
                const formattedOmit = omitPart;

                const intro = isExamplePhraseOfThisType ?
                    word_part_deletion_copy.intro_instruction_2({
                      word: originalWord, 
                      omit: formattedOmit, 
                      target: targetPart, 
                      skipTranslationWord: skipTranslation, 
                      skipTranslationTarget: skipTranslation, 
                      skipTranslationOmit: skipTranslation && !omitPart.includes('skipTranslation')
                    }) :
                    word_part_deletion_copy.actual_instruction({
                      word: originalWord, 
                      omit: formattedOmit, 
                      skipTranslationWord: skipTranslation, 
                      skipTranslationOmit: skipTranslation && !omitPart.includes('skipTranslation')
                    })

                // Use unified parsing that handles both skip translation and phoneme tags
                const onEnd = () => {
                    speech.startListening();
                    startTime = Date.now()
                    setTimeout(() => {
                        startPhrase()
                    }, 300)
                }

                const speechItems = parseTextWithSkipTranslationAndPhonemes(intro, getUserData().bilingualMode);

                if (speechItems.length > 0) {
                    // Check if any items contain phonemes
                    const hasPhonemes = speechItems.some(item => item.type === 'phoneme');
                                        
                    if (hasPhonemes) {
                        // Use speakWithPhoneme for mixed content
                        speakWithPhoneme(speechItems, onEnd);
                    } else {
                        // Use speakSequence for text-only content with skip translation
                        const textItems = speechItems.map(item => ({
                            textToSpeak: item.value,
                            skipTranslation: item.skipTranslation
                        }));
                                                
                        let bilingualSequenceToSpeak = {
                            textToSpeak: textItems,
                            passThroughSpeechTags: true,
                            onEnd
                        };
                        speech.speakSequence(bilingualSequenceToSpeak);
                    }
                } else {
                    // Fallback to regular speak if no parsing results
                    let introSpeech = {
                        textToSpeak: intro,
                        passThroughSpeechTags: true,
                        onEnd
                    };
                    speech.speak(introSpeech, true);
                }
            }

            playInstruction()
        }
    }, [isPlayingVerbalInstructions, hasPendingEndingSequence, currentStoryLoaded])

    useEffect(() => {
        if(isListening){
            console.log("⏳ Time lapse from end of speech to listening", Date.now() - startTime)
        }
    }, [isListening])

    /**
     * handle when the phrase is paused, play the re-prompt
     */
    useEffect(() => {
        // if the phrase is paused, and we should handle the pause, and we haven't handled the pause yet
        if(paused && shouldHandlePause && !didHandlePause.current){
            handlePause()
            didHandlePause.current = true
        }
    }, [paused, shouldHandlePause, didHandlePause.current])

    /**
     * handle when a transcript is received, play the finish speech and pass the pause control back to reader manager
     */
    useEffect(()=>{
        if(didAttempt){
            passPauseControlBackToReaderManager() // pass the pause control back to reader manager so it can handle the next pause
            registerEndingSpeech() // register the ending speech for reader manager to play on end of phrase
        }
    }, [didAttempt])

    /**
     * handles the re-prompt speech and passing the pause control back to reader manager
     */
    const handlePause = () => {
        const word_part_deletion_copy = getCopyByGradeband(COPY_STRINGS, 'word_part_deletion');
        // If omitPart does not contain a phoneme tag, we need to wrap it in quotes for GenAI TTS
        const formattedOmit = omitPart.includes('<phoneme') ? omitPart : `"${omitPart}"`;

        const skipTranslation = getUserData().bilingualMode;
        const inText = word_part_deletion_copy.prompt_to_try({
          word: originalWord, 
          omit: formattedOmit, 
          skipTranslationWord: skipTranslation, 
          skipTranslationOmit: skipTranslation && !omitPart.includes('skipTranslation')
        });
        const onEnd = () => {
            startTime = Date.now()
            passPauseControlBackToReaderManager() // pass the pause control back to reader manager so it can handle the next pause
            finishHandlePause() // let reader manager know we are done handling this pause
        }

        // Use unified parsing that handles both skip translation and phoneme tags
        const speechItems = parseTextWithSkipTranslationAndPhonemes(inText, getUserData().bilingualMode);

        if (speechItems.length > 0) {
            // Check if any items contain phonemes
            const hasPhonemes = speechItems.some(item => item.type === 'phoneme');
            
            if (hasPhonemes) {
                // Use speakWithPhoneme for mixed content
                speakWithPhoneme(speechItems, onEnd);
            } else {
                // Use speakSequence for text-only content with skip translation
                const textItems = speechItems.map(item => ({
                    textToSpeak: item.value,
                    skipTranslation: item.skipTranslation
                }));
                
                let bilingualSequenceToSpeak = {
                    textToSpeak: textItems,
                    passThroughSpeechTags: true,
                    onEnd
                };
                speech.speakSequence(bilingualSequenceToSpeak);
            }
        } else {
            // Fallback to regular speak if no parsing results
            speech.speak({
                textToSpeak: inText,
                onEnd
            });
        }
    }

    /**
     * register an ending speech for reader manager to play on end of phrase
     */
    const registerEndingSpeech = () => {
        const word_part_deletion_copy = getCopyByGradeband(COPY_STRINGS, 'word_part_deletion');
        addToEndingSequence({...bark})
        if(isExamplePhraseOfThisType){
            addToEndingSequence({textToSpeak: word_part_deletion_copy.intro_end})
        }else if(isLastPhraseOfThisType){
            if(isLastPageOfStory){
                addToEndingSequence({textToSpeak: word_part_deletion_copy.end})
            }else{
                addToEndingSequence({textToSpeak: word_part_deletion_copy.end_with_more_activities})
            }
        }
    }

    /**
     * all we will see is Spot in the awaken state throughout the activity
     */
    return (
        <Spot spotState={spotState}/>
    )
}
